import { AppState } from "@Store/index";
import { useState, useEffect } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { updateInstallmentPlans } from "@Pages/PropertyPage/data/propertyPageReducer";
import fetchInstallmentPlans from "../../data/fetchInstallmentPlans";
import { ActiveFilterType } from "@Pages/Search/components/FilterSection/types";
import DesktopInstalmentSection from "./DesktopInstalmentSection";
import MobileInstalmentSection from "./MobileInstalmentSection";
import { getInstallmentTabData } from "./utils";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import config from "@Config/index";
import useIsLanguage from "hooks/useIsLanguage";

const InstalmentSection = ({ isDesktop = false, isModal = false }) => {
  const { features, inventoryId, regionId, installmentPlans } = useSelector(
    ({ propertyPageData, bookFormData }: AppState) => ({
      features: propertyPageData?.data?.features || bookFormData?.data?.features,
      amenitiesImages: propertyPageData?.data?.images
        ? propertyPageData?.data?.images.filter(
            ({ type }: { type: string }) => type === "amenities",
          )
        : [],
      inventoryId: propertyPageData?.data?.id || bookFormData.data?.id,
      regionId: propertyPageData?.data?.region_id || bookFormData.data?.region_id,
      installmentPlans: propertyPageData?.installment_plans || [],
    }),
    shallowEqual,
  );
  const userLanguage = useIsLanguage();
  const showBillsIncluded =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_BILLS_INCLUDED) || config.IS_CHINA_APP;

  const [showModal, setShowModal] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState("All");
  const [selectedYear, setSelectedYear] = useState("All");
  const [activeTab, setActiveTab] = useState("all");
  const featuresData = [...(features || [])];
  const sortedData = [...featuresData?.sort((a: any, b: any) => a?.name?.localeCompare(b?.name))];
  const filteredData: any[] = [];
  const [showBillsModal, setShowBillsModal] = useState(false);

  const prefixContent = null;
  const suffixContent = null;
  const amenities: any[] = [];

  const calculateTotalPrice = (amenitiesList: any[]) =>
    amenitiesList.reduce((sum, amenity) => {
      const price = Number(amenity.price);
      const fixedPrice = price && !Number.isNaN(price) ? parseFloat(price.toFixed(1)) : 0;
      return sum + fixedPrice;
    }, 0);

  const totalCalculatedPrice = calculateTotalPrice(amenities);

  const allAmenitiesHaveNAPrices =
    amenities.length === 0 ||
    amenities.every((amenity: any) => {
      const price = Number(amenity.price);
      return !price || Number.isNaN(price);
    }) ||
    isModal ||
    !showBillsIncluded;

  const hasEstimatedItems = amenities.some((amenity: any) => {
    const price = Number(amenity.price);
    return !price || Number.isNaN(price);
  });

  const handleModalClose = () => {
    setShowModal(false);
  };

  const { t } = useTranslationHook();

  const [activeFilter, setActiveFilter] = useState<ActiveFilterType>("");

  const propertyPageDispatch = useDispatch();

  const hideBottomSheetOverlay = () => {
    setActiveFilter("");
  };

  const handleClearClick = () => {
    setActiveFilter("");
  };

  useEffect(() => {
    const fetchPlans = async () => {
      if (!inventoryId || !regionId) return;
      // Skip client-side fetch if installment plans were already loaded via SSR
      if (installmentPlans && installmentPlans.length > 0) return;
      try {
        const response = await fetchInstallmentPlans({
          id: inventoryId,
          regionId,
          selectedLang: userLanguage,
        });
        const rawPlans = response.data?.data?.result;
        propertyPageDispatch(updateInstallmentPlans({ installment_plans: rawPlans }));
      } catch (error) {
        console.error("Error fetching installment plans:", error);
      }
    };

    fetchPlans();
  }, [inventoryId, regionId]);

  const durations = Array.from(
    new Set(
      installmentPlans
        .filter((plan: any) => plan.duration && plan.duration_unit)
        .map((plan: any) => `${plan.duration} ${plan.duration_unit}`),
    ),
  ).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  const years = Array.from(
    new Set(installmentPlans.map((plan: any) => plan.academic_year).filter(Boolean)),
  ).sort((a, b) => {
    const [aStart] = a.replace("ay_", "").split("_").map(Number);
    const [bStart] = b.replace("ay_", "").split("_").map(Number);
    return aStart - bStart;
  });

  const filtersData = {
    duration: durations,
    year: years,
  };

  const filteredInstallmentPlans = installmentPlans.filter((plan: any) =>
    selectedYear === "All" ? plan : plan.academic_year === selectedYear,
  );

  const tabdata = getInstallmentTabData(filteredInstallmentPlans);

  useEffect(() => {
    if ((tabdata as Record<string, any[]>)[activeTab]?.length === 0) {
      const firstNonEmptyTab =
        Object.keys(tabdata).find((tab) => (tabdata as Record<string, any[]>)[tab]?.length > 0) ||
        "all";
      setActiveTab(firstNonEmptyTab);
    }
  }, [tabdata, activeTab]);

  const hasInstallmentPlans = installmentPlans?.length > 0 && installmentPlans !== null;
  const hasBillsIncluded = filteredData && filteredData?.length > 0;
  if (!hasInstallmentPlans) {
    return null;
  }

  // bills included modal
  const handleBillsModalOpen = () => {
    try {
      gtagEventEmitter({
        action: "viewed_amenities_prices",
        category: PAGE.INVENTORY,
        label: "success",
      });
    } catch (error) {
      console.error("Error emitting gtag event:", error);
    }
    setShowBillsModal(true);
  };

  const handleBillsModalClose = () => {
    setShowBillsModal(false);
  };

  const commonProps = {
    filteredData,
    sortedData,
    showModal,
    handleModalClose,
    selectedDuration,
    setSelectedDuration,
    selectedYear,
    setSelectedYear,
    filtersData,
    installmentPlans,
    isModal,
    t,
    activeFilter,
    hideBottomSheetOverlay,
    handleClearClick,
    tabdata,
    setActiveTab,
    activeTab,
    isDesktop,
    hasInstallmentPlans,
    handleBillsModalOpen,
    handleBillsModalClose,
    showBillsModal,
    prefixContent,
    suffixContent,
    amenities,
    totalCalculatedPrice,
    allAmenitiesHaveNAPrices,
    hasBillsIncluded,
    hasEstimatedItems,
  };

  return isDesktop ? (
    <DesktopInstalmentSection {...commonProps} />
  ) : (
    <MobileInstalmentSection {...commonProps} />
  );
};

const InstalmentSectionWrapper = (props: any) => {
  const isBillsAndPayments = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.BILLS_AND_PAYMENTS);
  return isBillsAndPayments || config.IS_CHINA_APP ? <InstalmentSection {...props} /> : <></>;
};

export default wrapperHOC(InstalmentSectionWrapper, {
  componentName: "InstalmentSection-PropertyPage",
  showForChina: true,
});
